import { useHistory } from 'react-router-dom';
import {
  StyledButtons,
  StyledButtonWithDropdown,
  StyledLoadingButton,
  StyledPageHeader,
  StyledToolbar,
} from './styles';
import { SMARTCONNECTORS_PATHNAME } from '__pages/SmartConnectors/constants';
import { useTranslation } from 'react-i18next';
import { FC, useMemo, useState } from 'react';
import { useRunSmartConnectorModal } from '__pages/SmartConnectors/components/RunSmartConnectorModal/useRunSmartConnectorModal';
import { RunSmartConnectorModal } from '__pages/SmartConnectors/components/RunSmartConnectorModal';
import { SmartConnector } from '__pages/SmartConnectors/types';
import SmartConnectorService from 'services/SmartConnectorService';
import { useToast } from '__components/ToastProvider';
import { getErrorMessage } from '__hooks/useErrors';
import { getOriginalError } from '__services/AxiosService';
import useModal from '__components/Modals/useModal';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import { toastVariant } from '__components/ToastProvider';
import { getIsSmartConnectorActive } from '__pages/SmartConnectors/helpers';

export const ControlBar: FC<{
  smartConnector: SmartConnector;
  access: { edit: boolean; remove: boolean };
  onStartRun: () => void;
}> = ({ smartConnector, access, onStartRun }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [showToast] = useToast();

  const [deleting, setDeleting] = useState(false);

  const { runSmartConnectorModalProps, showRunSmartConnectorModal } =
    useRunSmartConnectorModal(onStartRun);

  const runSmartConnector = (isDryRun: boolean) => {
    showRunSmartConnectorModal(
      smartConnector,
      isDryRun,
      smartConnector.is_diff_check_enabled
    );
  };

  const startRun = () => {
    runSmartConnector(false);
  };

  const startDryRun = () => {
    runSmartConnector(true);
  };

  const handleAction = ({ value }: { value: 'dry_run' }) => {
    if (value === 'dry_run') {
      runSmartConnector(true);
    }
  };

  const actionOptions = useMemo(
    () => [{ value: 'dry_run', label: t('Start Dry Run') }],
    [t]
  );

  const deleteSmartConector = async () => {
    setDeleting(true);
    try {
      await SmartConnectorService.deleteSmartConnector(smartConnector.id!);
      history.replace(SMARTCONNECTORS_PATHNAME);
    } catch (err) {
      showToast({
        message:
          getErrorMessage(getOriginalError(err)) ||
          t('Failted to delete smartConnector'),
        variant: toastVariant.FAILURE,
      });
    } finally {
      setDeleting(false);
    }
  };

  const [deleteModalProps, deleteButtonProps] = useModal({
    handleSubmit: deleteSmartConector,
  });

  return (
    <>
      <StyledToolbar
        backLabel={t('back to smartConnectors')}
        goBackPath={SMARTCONNECTORS_PATHNAME}
      >
        <StyledPageHeader type="header">{smartConnector.name}</StyledPageHeader>
        <StyledButtons>
          {access.remove ? (
            <StyledLoadingButton
              color="red"
              {...deleteButtonProps}
              disabled={deleting}
              loading={deleting}
            >
              {t('Delete')}
            </StyledLoadingButton>
          ) : null}
          {access.edit ? (
            getIsSmartConnectorActive(smartConnector) ? (
              <StyledButtonWithDropdown
                color="green"
                onClick={startRun}
                onChange={handleAction}
                options={actionOptions}
              >
                {t('Start Run')}
              </StyledButtonWithDropdown>
            ) : (
              <StyledLoadingButton color="green" onClick={startDryRun}>
                {t('Start Dry Run')}
              </StyledLoadingButton>
            )
          ) : null}
        </StyledButtons>
      </StyledToolbar>
      {runSmartConnectorModalProps.show ? (
        <RunSmartConnectorModal {...runSmartConnectorModalProps} />
      ) : null}
      <ConfirmDeletionModal {...deleteModalProps}>
        {t(
          'This will permanently delete the SmartConnector and uploads will no longer be able to be processed through these steps.'
        )}
      </ConfirmDeletionModal>
    </>
  );
};
